/**
 * AdminExchanges
 * Admin management for the predefined exchange catalog.
 * Admins can only enable / disable exchanges — they cannot create or delete them.
 */
import { useEffect, useState } from 'react';
import { useLingui } from '@lingui/react';
import { useApiClient } from '../api/client';
import type { Exchange } from '../api/client';

export default function AdminExchanges() {
  const { _ } = useLingui();
  const api = useApiClient();
  const [exchanges, setExchanges] = useState<Exchange[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function reload() {
    setLoading(true);
    try {
      setExchanges(await api.adminListExchanges());
    } catch {
      setError(_({ id: 'admin.exchanges.error.listFetch' }));
    } finally {
      setLoading(false);
    }
  }

  async function handleToggle(ex: Exchange) {
    try {
      await api.adminUpdateExchange(ex.id, { isActive: !ex.isActive });
      await reload();
    } catch {
      setError(_({ id: 'admin.exchanges.error.updateFailed' }));
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between min-h-[28px]">
        <h1 className="text-xl font-bold hidden lg:block">{_({ id: 'admin.exchanges.title' })}</h1>
      </div>

      {error && (
        <div className="alert alert-error">
          <span>{error}</span>
          <button className="btn btn-ghost btn-xs" onClick={() => setError(null)}>
            ✕
          </button>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-10">
          <span className="loading loading-spinner loading-lg" />
        </div>
      ) : (
        <div className="card bg-base-100 shadow border border-base-300">
          <div className="overflow-x-auto">
            <table className="table table-zebra min-w-[460px]">
              <thead>
                <tr>
                  <th>{_({ id: 'admin.exchanges.table.colSlug' })}</th>
                  <th>{_({ id: 'admin.exchanges.table.colName' })}</th>
                  <th>{_({ id: 'admin.exchanges.table.colCredentials' })}</th>
                  <th>{_({ id: 'admin.exchanges.table.colStatus' })}</th>
                </tr>
              </thead>
              <tbody>
                {exchanges.length === 0 && (
                  <tr>
                    <td colSpan={4} className="text-center text-base-content/50 py-8">
                      {_({ id: 'admin.exchanges.table.empty' })}
                    </td>
                  </tr>
                )}
                {exchanges.map((ex) => (
                  <tr key={ex.id}>
                    <td>
                      <code className="bg-base-200 px-1 rounded text-sm">{ex.slug}</code>
                    </td>
                    <td className="font-medium">{ex.name}</td>
                    <td>
                      <div className="flex flex-wrap gap-1">
                        {ex.requiredCredentials.map((c) => (
                          <span key={c} className="badge badge-outline badge-sm">
                            {c}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td>
                      <input
                        type="checkbox"
                        className="toggle toggle-success toggle-sm"
                        checked={ex.isActive}
                        onChange={() => void handleToggle(ex)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
